import { useState } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../../shared/auth/AuthContext';
import { authApi } from '../../shared/api/auth';
import type { InvitationPreview } from '../../shared/types/auth';
import { AuthLayout } from '../components/AuthLayout';

const nameFields = {
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
};

const withPasswordSchema = z
  .object({
    ...nameFields,
    password: z.string().min(8, 'Use at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords must match',
  });

const noPasswordSchema = z.object(nameFields);

type WithPasswordValues = z.infer<typeof withPasswordSchema>;
type NoPasswordValues = z.infer<typeof noPasswordSchema>;

export default function AcceptInvitationPage(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const preview = useQuery({
    queryKey: ['invitation-preview', token],
    queryFn: () => authApi.previewInvitation(token),
    enabled: Boolean(token),
    retry: false,
  });

  return (
    <AuthLayout title="Accept invitation">
      <Paper sx={{ p: 4, width: '100%', maxWidth: 400 }} elevation={2}>
        <Typography variant="h5" gutterBottom>
          Set up your account
        </Typography>
        {!token ? (
          <Stack spacing={2}>
            <Alert severity="error">This invitation link is missing required information.</Alert>
            <Link component={RouterLink} to="/login">
              Back to sign in
            </Link>
          </Stack>
        ) : preview.isPending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : preview.isError || !preview.data ? (
          <Stack spacing={2}>
            <Alert severity="error">This invitation link is invalid or has expired.</Alert>
            <Link component={RouterLink} to="/login">
              Back to sign in
            </Link>
          </Stack>
        ) : (
          <InvitationForm token={token} preview={preview.data} />
        )}
      </Paper>
    </AuthLayout>
  );
}

function InvitationForm({ token, preview }: { token: string; preview: InvitationPreview }): JSX.Element {
  const { acceptInvitation } = useAuth();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);

  const defaultValues = {
    firstName: preview.firstName ?? '',
    lastName: preview.lastName ?? '',
  };

  const submit = async (firstName: string, lastName: string, password: string | null): Promise<void> => {
    setServerError(null);
    try {
      const profile = await acceptInvitation(token, password, firstName, lastName);
      navigate(profile.mustCompleteProfile ? '/profile' : '/', { replace: true });
    } catch {
      setServerError('This invitation link is invalid or has expired.');
    }
  };

  return preview.passwordRequired ? (
    <PasswordForm defaultValues={defaultValues} serverError={serverError} onSubmit={submit} />
  ) : (
    <ExistingAccountForm
      defaultValues={defaultValues}
      tenantName={preview.tenantName}
      serverError={serverError}
      onSubmit={(firstName, lastName) => submit(firstName, lastName, null)}
    />
  );
}

interface FormProps {
  defaultValues: { firstName: string; lastName: string };
  serverError: string | null;
}

function PasswordForm({
  defaultValues,
  serverError,
  onSubmit,
}: FormProps & { onSubmit: (firstName: string, lastName: string, password: string) => void }): JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<WithPasswordValues>({ resolver: zodResolver(withPasswordSchema), defaultValues });

  return (
    <Box component="form" onSubmit={handleSubmit((v) => onSubmit(v.firstName, v.lastName, v.password))} noValidate>
      <Stack spacing={2}>
        {serverError && <Alert severity="error">{serverError}</Alert>}
        <TextField
          label="First name"
          autoComplete="given-name"
          error={Boolean(errors.firstName)}
          helperText={errors.firstName?.message}
          {...register('firstName')}
        />
        <TextField
          label="Last name"
          autoComplete="family-name"
          error={Boolean(errors.lastName)}
          helperText={errors.lastName?.message}
          {...register('lastName')}
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="new-password"
          error={Boolean(errors.password)}
          helperText={errors.password?.message}
          {...register('password')}
        />
        <TextField
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          error={Boolean(errors.confirmPassword)}
          helperText={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />
        <Button type="submit" variant="contained" disabled={isSubmitting} size="large">
          {isSubmitting ? 'Creating account…' : 'Create account'}
        </Button>
      </Stack>
    </Box>
  );
}

function ExistingAccountForm({
  defaultValues,
  tenantName,
  serverError,
  onSubmit,
}: FormProps & { tenantName: string; onSubmit: (firstName: string, lastName: string) => void }): JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<NoPasswordValues>({ resolver: zodResolver(noPasswordSchema), defaultValues });

  return (
    <Box component="form" onSubmit={handleSubmit((v) => onSubmit(v.firstName, v.lastName))} noValidate>
      <Stack spacing={2}>
        {serverError && <Alert severity="error">{serverError}</Alert>}
        <Alert severity="info">
          You already have an ExamFolio login. Accepting this invitation adds{' '}
          <strong>{tenantName}</strong> to your account — sign in afterward with your existing password.
        </Alert>
        <TextField
          label="First name"
          autoComplete="given-name"
          error={Boolean(errors.firstName)}
          helperText={errors.firstName?.message}
          {...register('firstName')}
        />
        <TextField
          label="Last name"
          autoComplete="family-name"
          error={Boolean(errors.lastName)}
          helperText={errors.lastName?.message}
          {...register('lastName')}
        />
        <Button type="submit" variant="contained" disabled={isSubmitting} size="large">
          {isSubmitting ? 'Joining…' : 'Accept invitation'}
        </Button>
      </Stack>
    </Box>
  );
}
